import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Trash2, X, Sticker as StickerIcon, Sparkles } from "lucide-react";
import {
    STICKER_LIBRARY, STICKER_PRESETS, PRESET_COLORS,
    MAX_STICKERS, MIN_SCALE, MAX_SCALE, DEFAULT_SCALE,
    drawSticker, stickerHit,
} from "@/lib/sticker-lib";

const OUT_W = 1200;

/**
 * Sticker editor for the "Upload My Own" tab. Renders the merchant's uploaded
 * image on a 4:3 canvas and lets them layer 6 sticker types on top. On save,
 * exports a PNG blob and calls onSave(file) so the caller can replace the
 * posterFile / posterPreview in the wizard.
 */
export function StickerEditor({ open, onOpenChange, imageUrl, onSave }) {
    const canvasRef = useRef(null);
    const [img, setImg] = useState(null);
    const [size, setSize] = useState({ w: OUT_W, h: (OUT_W * 3) / 4 });
    const [stickers, setStickers] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [saving, setSaving] = useState(false);
    const dragRef = useRef({ id: null, offsetX: 0, offsetY: 0 });
    const stickersRef = useRef([]);
    useEffect(() => { stickersRef.current = stickers; }, [stickers]);

    // Load the base image whenever the URL changes
    useEffect(() => {
        if (!open || !imageUrl) return;
        const im = new Image();
        im.crossOrigin = "anonymous";
        im.onload = () => {
            const ratio = im.height / im.width;
            const h = Math.round(OUT_W * ratio);
            setSize({ w: OUT_W, h });
            setImg(im);
        };
        im.onerror = () => toast.error("Couldn't load base image");
        im.src = imageUrl;
        setStickers([]); setSelectedId(null);
    }, [imageUrl, open]);

    // Redraw on any change
    useEffect(() => {
        const c = canvasRef.current;
        if (!c || !img) return;
        c.width = size.w; c.height = size.h;
        const ctx = c.getContext("2d");
        ctx.clearRect(0, 0, size.w, size.h);
        ctx.drawImage(img, 0, 0, size.w, size.h);
        for (const s of stickers) drawSticker(ctx, s, size.w, size.h);
        if (selectedId) {
            const s = stickers.find((x) => x.id === selectedId);
            if (s) {
                const w = s.scale * size.w;
                ctx.save();
                ctx.strokeStyle = "#FF7A1A";
                ctx.lineWidth = 4;
                ctx.setLineDash([12, 8]);
                ctx.strokeRect(s.x_percent * size.w - w / 2, s.y_percent * size.h - w / 2, w, w);
                ctx.restore();
            }
        }
    }, [img, size, stickers, selectedId]);

    const canvasCoords = (evt) => {
        const c = canvasRef.current;
        const rect = c.getBoundingClientRect();
        const clientX = evt.clientX ?? evt.touches?.[0]?.clientX ?? 0;
        const clientY = evt.clientY ?? evt.touches?.[0]?.clientY ?? 0;
        return {
            x: ((clientX - rect.left) / rect.width) * size.w,
            y: ((clientY - rect.top) / rect.height) * size.h,
        };
    };
    const addSticker = (tpl) => {
        if (stickers.length >= MAX_STICKERS) { toast.error(`Max ${MAX_STICKERS} stickers`); return; }
        const id = `st_${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
        const next = { id, kind: tpl.kind, text: tpl.text, color: tpl.color, x_percent: 0.5, y_percent: 0.5, scale: DEFAULT_SCALE };
        setStickers((prev) => [...prev, next]);
        setSelectedId(id);
    };
    const applyPreset = (preset) => {
        const withIds = preset.stickers.map((s, i) => ({ ...s, id: `st_${Date.now()}_${i}` }));
        setStickers(withIds);
        setSelectedId(null);
    };
    const updateSticker = (id, patch) => setStickers((prev) => prev.map((s) => (s.id === id ? { ...s, ...patch } : s)));
    const deleteSticker = (id) => { setStickers((prev) => prev.filter((s) => s.id !== id)); if (selectedId === id) setSelectedId(null); };

    const onDown = (evt) => {
        const list = stickersRef.current;
        if (!list.length) return;
        const { x, y } = canvasCoords(evt);
        for (let i = list.length - 1; i >= 0; i--) {
            const s = list[i];
            if (stickerHit(s, size.w, size.h, x, y)) {
                dragRef.current = { id: s.id, offsetX: x - s.x_percent * size.w, offsetY: y - s.y_percent * size.h };
                setSelectedId(s.id);
                try { evt.currentTarget.setPointerCapture(evt.pointerId); } catch { /* no-op */ }
                evt.preventDefault();
                return;
            }
        }
        setSelectedId(null);
    };
    const onMove = (evt) => {
        if (!dragRef.current.id) return;
        const { x, y } = canvasCoords(evt);
        const nx = (x - dragRef.current.offsetX) / size.w;
        const ny = (y - dragRef.current.offsetY) / size.h;
        updateSticker(dragRef.current.id, {
            x_percent: Math.min(0.95, Math.max(0.05, nx)),
            y_percent: Math.min(0.95, Math.max(0.05, ny)),
        });
    };
    const onUp = () => { dragRef.current = { id: null, offsetX: 0, offsetY: 0 }; };

    const save = async () => {
        const c = canvasRef.current;
        if (!c || !img) return;
        setSaving(true);
        try {
            // Final export — redraw WITHOUT the selection outline
            const ctx = c.getContext("2d");
            ctx.clearRect(0, 0, size.w, size.h);
            ctx.drawImage(img, 0, 0, size.w, size.h);
            for (const s of stickers) drawSticker(ctx, s, size.w, size.h);
            const blob = await new Promise((res) => c.toBlob(res, "image/png", 0.92));
            if (!blob) throw new Error("Canvas export failed");
            const file = new File([blob], `poster-stickered-${Date.now()}.png`, { type: "image/png" });
            onSave?.(file, URL.createObjectURL(blob));
            onOpenChange(false);
        } catch (e) {
            toast.error(e.message || "Sticker save failed");
        } finally { setSaving(false); }
    };

    const selected = stickers.find((s) => s.id === selectedId) || null;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-5xl" data-testid="sticker-editor-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2 text-xl font-extrabold">
                        <StickerIcon size={18} /> Add Stickers
                    </DialogTitle>
                </DialogHeader>
                <div className="grid gap-4 lg:grid-cols-12">
                    <div className="lg:col-span-7">
                        <div className="overflow-hidden rounded-2xl border border-neutral-200 bg-neutral-100">
                            <canvas
                                ref={canvasRef}
                                className="block h-auto w-full touch-none"
                                data-testid="sticker-editor-canvas"
                                onPointerDown={onDown}
                                onPointerMove={onMove}
                                onPointerUp={onUp}
                                onPointerCancel={onUp}
                            />
                        </div>
                        <p className="mt-2 text-[11px] text-neutral-500">Tap a sticker to select · drag to move · use the panel to resize / edit / delete.</p>
                    </div>
                    <div className="lg:col-span-5">
                        <div className="mb-2 flex items-center justify-between text-xs font-semibold text-neutral-600">
                            <span className="flex items-center gap-1"><Sparkles size={12} /> Sticker library</span>
                            <span className="text-[10px] text-neutral-400">{stickers.length}/{MAX_STICKERS}</span>
                        </div>
                        <div className="grid grid-cols-3 gap-1" data-testid="sticker-lib-upload">
                            {STICKER_LIBRARY.map((t) => (
                                <button
                                    key={t.kind}
                                    type="button"
                                    onClick={() => addSticker(t)}
                                    data-testid={`sticker-upload-add-${t.kind}`}
                                    disabled={stickers.length >= MAX_STICKERS}
                                    className="rounded-lg border border-neutral-200 bg-white p-2 text-[10px] font-bold text-neutral-800 transition hover:border-orange-400 hover:bg-orange-50 disabled:opacity-40"
                                >
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <div className="mt-3">
                            <div className="mb-1 text-[10px] font-semibold uppercase text-neutral-500">Preset examples</div>
                            <div className="flex flex-wrap gap-1">
                                {STICKER_PRESETS.map((p) => (
                                    <button
                                        key={p.id}
                                        type="button"
                                        onClick={() => applyPreset(p)}
                                        data-testid={`sticker-upload-preset-${p.id}`}
                                        className="rounded-full border border-orange-200 bg-orange-50 px-2 py-1 text-[10px] font-semibold text-orange-700 hover:bg-orange-100"
                                    >
                                        {p.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        {selected && (
                            <div className="mt-4 rounded-xl border border-orange-200 bg-orange-50 p-3" data-testid="sticker-upload-editor">
                                <div className="mb-2 flex items-center justify-between">
                                    <span className="text-[11px] font-bold uppercase text-orange-700">Edit sticker</span>
                                    <button type="button" onClick={() => setSelectedId(null)} className="text-neutral-500 hover:text-neutral-800">
                                        <X size={14} />
                                    </button>
                                </div>
                                <Label className="text-xs">Text</Label>
                                <Input
                                    value={selected.text}
                                    onChange={(e) => updateSticker(selected.id, { text: e.target.value })}
                                    maxLength={20}
                                    className="mb-2"
                                    data-testid="sticker-upload-text"
                                />
                                <Label className="text-xs">Size</Label>
                                <Slider
                                    value={[Math.round(selected.scale * 100)]}
                                    min={Math.round(MIN_SCALE * 100)} max={Math.round(MAX_SCALE * 100)} step={1}
                                    onValueChange={([v]) => updateSticker(selected.id, { scale: v / 100 })}
                                    data-testid="sticker-upload-size"
                                />
                                <div className="mt-2">
                                    <Label className="text-xs">Color</Label>
                                    <div className="mt-1 flex gap-1">
                                        {PRESET_COLORS.map((c) => (
                                            <button
                                                key={c}
                                                type="button"
                                                onClick={() => updateSticker(selected.id, { color: c })}
                                                data-testid={`sticker-upload-color-${c.replace("#", "")}`}
                                                className={`h-7 w-7 rounded-full border-2 shadow ${selected.color === c ? "border-neutral-900" : "border-white"}`}
                                                style={{ background: c }}
                                                aria-label={c}
                                            />
                                        ))}
                                    </div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => deleteSticker(selected.id)}
                                    data-testid="sticker-upload-delete"
                                    className="mt-3 inline-flex items-center gap-1 rounded-full border border-red-300 bg-white px-3 py-1 text-[11px] font-semibold text-red-600 hover:bg-red-50"
                                >
                                    <Trash2 size={12} /> Delete
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                <DialogFooter>
                    <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="sticker-editor-cancel">Cancel</Button>
                    <Button onClick={save} disabled={saving} className="nest-gradient text-white" data-testid="sticker-editor-save">
                        {saving ? "Saving…" : "Save with stickers"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default StickerEditor;
